import { Tooltip as MuiTooltip, SvgIcon } from "@mui/material";
import type React from "react";
import { useRef, useState } from "react";
import { trackEvent } from "../../utils/analytics";
import Styles from "./Tooltip.module.scss";

// Functional / Categorical: info icon palette (mint bg + neutral gray glyph). Stays stable across user themes.
const INFO_ICON = { BG: "#dbfceb", GLYPH: "rgb(114, 115, 120)" } as const;

type Props = {
	id: string;
	text: React.ReactNode;
	component: React.ReactElement;
	showIcon?: boolean;
	showTooltip?: boolean;
	onOpen?: () => void;
	onClose?: () => void;
	backgroundColor?: string;
	placement?: "top" | "bottom" | "left" | "right";
};

export const Tooltip = ({ text, component, showIcon = true, showTooltip = false, onOpen, onClose, backgroundColor, placement = "top" }: Props) => {
	const [isTooltipVisible, setIsTooltipVisible] = useState(false);

	const handleMouseEnter = () => setIsTooltipVisible(true);
	const handleMouseLeave = () => setIsTooltipVisible(false);

	const tooltipRef = useRef<HTMLDivElement | null>(null);

	const toggleTooltip = () => {
		setIsTooltipVisible(!isTooltipVisible);
	};

	return (
		<div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
			<MuiTooltip
				placement={placement}
				arrow
				enterTouchDelay={0}
				leaveTouchDelay={1500}
				onOpen={onOpen}
				open={showTooltip && isTooltipVisible}
				onClose={onClose}
				title={<div style={{ fontSize: "14px" }}>{text}</div>}
				slotProps={{
					tooltip: backgroundColor
						? {
								sx: {
									bgcolor: backgroundColor,
									"& .MuiTooltip-arrow": {
										color: backgroundColor,
									},
								},
							}
						: {},
				}}
			>
				{renderChildren(showIcon, tooltipRef, toggleTooltip, component)}
			</MuiTooltip>
		</div>
	);
};
function renderChildren(
	showIcon: boolean,
	tooltipRef: React.MutableRefObject<HTMLDivElement | null>,
	toggleTooltip: () => void,
	component: React.ReactElement<any, string | React.JSXElementConstructor<any>>,
) {
	return (
		<div className={Styles.tooltipContainer} style={showIcon ? { position: "relative" } : {}} ref={tooltipRef}>
			<div onClick={showIcon ? () => {} : toggleTooltip}>{component}</div>
			{showIcon && (
				<SvgIcon sx={{ mx: 0.5 }}>
					<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill={INFO_ICON.BG} />
					<path d="M13 9h-2V7h2v2zm0 2h-2v6h2v-6z" fill={INFO_ICON.GLYPH} />
				</SvgIcon>
			)}
		</div>
	);
}
